import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFAutocomplete, RHFTextField, RHFUploadAvatar, RHFSwitch } from 'src/components/hook-form';
import axiosInstance, { API_ENDPOINTS } from 'src/utils/axios';
import Typography from '@mui/material/Typography';
import { fData } from 'src/utils/format-number';
import Grid from '@mui/system/Unstable_Grid/Grid';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import { ASSETS_API } from 'src/config-global';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Iconify from 'src/components/iconify';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';

export default function DishQuickForm({ current, open, onClose, isEdit }) {
  const { enqueueSnackbar } = useSnackbar();

  const schema = Yup.object().shape({
    portugueseName: Yup.string().required("Campo obrigatório."),
    frenchName: Yup.string().optional(),
    englishName: Yup.string().optional(),
    portugueseDescription: Yup.string().optional().nullable(),
    frenchDescription: Yup.string().optional().nullable(),
    englishDescription: Yup.string().optional().nullable(),
    portugueseNotes: Yup.string().optional().nullable(),
    frenchNotes: Yup.string().optional().nullable(),
    englishNotes: Yup.string().optional().nullable(),
    cost: Yup.string().when('hasDetailedPricing', {
      is: false,
      then: (s) => s.required("Campo obrigatório"),
      otherwise: (s) => s.optional()
    }),
    hasDetailedPricing: Yup.boolean(),
    category: Yup.object().shape({
      id: Yup.string().required("Is required"),
      portugueseName: Yup.string().required("Is required")
    }),
    photo: Yup.mixed().optional(),
    allergenIds: Yup.array().optional(),
    prices: Yup.array().of(
      Yup.object().shape({
        portugueseLabel: Yup.string().required("Etiqueta PT obrigatória"),
        frenchLabel: Yup.string().optional(),
        englishLabel: Yup.string().optional(),
        price: Yup.string().required("Preço obrigatório"),
        sortOrder: Yup.number().optional()
      })
    ).optional()
  });

  const defaultData = useMemo(
    () => ({
      id: current?.id || '',
      portugueseName: current?.portugueseName || '',
      frenchName: current?.frenchName || '',
      englishName: current?.englishName || '',
      portugueseDescription: current?.portugueseDescription || '',
      frenchDescription: current?.frenchDescription || '',
      englishDescription: current?.englishDescription || '',
      portugueseNotes: current?.portugueseNotes || '',
      frenchNotes: current?.frenchNotes || '',
      englishNotes: current?.englishNotes || '',
      cost: current?.cost?.toString() || '',
      hasDetailedPricing: current?.hasDetailedPricing || false,
      category: current?.category || null,
      photo: current?.photo ? `${ASSETS_API}${current?.photo}` : '',
      creationDate: current?.creationDate || '',
      modifiedAt: current?.modifiedAt || '',
      allergenIds: current?.allergens?.map(a => a.allergen || a) || [],
      prices: current?.prices?.map(p => ({
        id: p.id,
        portugueseLabel: p.portugueseLabel,
        frenchLabel: p.frenchLabel,
        englishLabel: p.englishLabel,
        price: p.price?.toString() || '',
        sortOrder: p.sortOrder
      })) || []
    }),
    [current]
  );

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultData,
  });

  const [dishCategoriesData, setDishCategoriesData] = useState([]);
  const [allergensData, setAllergensData] = useState([]);

  const {
    reset,
    watch,
    control,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const { fields: priceFields, append: appendPrice, remove: removePrice } = useFieldArray({
    control,
    name: 'prices'
  });

  const hasDetailedPricing = watch('hasDetailedPricing');

  const fetchDataDishCategory = async () => {
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.dishcategory.list);
      setDishCategoriesData(res.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchAllergens = async () => {
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.allergen.list);
      setAllergensData(res.data);
    } catch (error) {
      console.error('Error fetching allergens:', error);
    }
  };

  useEffect(() => {
    fetchDataDishCategory();
    fetchAllergens();
  }, []);

  const onSubmit = useCallback(
    async (data) => {
      try {
        const formData = new FormData();
        formData.append('id', data.id);
        formData.append('categoryId', data.category.id);
        formData.append('portugueseName', data.portugueseName);
        formData.append('frenchName', data.frenchName || '');
        formData.append('englishName', data.englishName || '');
        formData.append('portugueseDescription', data.portugueseDescription || '');
        formData.append('frenchDescription', data.frenchDescription || '');
        formData.append('englishDescription', data.englishDescription || '');
        formData.append('portugueseNotes', data.portugueseNotes || '');
        formData.append('frenchNotes', data.frenchNotes || '');
        formData.append('englishNotes', data.englishNotes || '');
        const costValue = data.hasDetailedPricing ? '0' : data.cost.toString().replace(',', '.');
        formData.append('cost', costValue);
        formData.append('hasDetailedPricing', data.hasDetailedPricing);
        formData.append('creationDate', data.creationDate || '');
        formData.append('modifiedAt', data.modifiedAt || '');

        // Add allergen IDs
        if (data.allergenIds && data.allergenIds.length > 0) {
          data.allergenIds.forEach((allergen, index) => {
            formData.append(`allergenIds[${index}]`, allergen.id);
          });
        }

        // Add prices if detailed pricing is enabled
        if (data.hasDetailedPricing && data.prices && data.prices.length > 0) {
          data.prices.forEach((price, index) => {
            if (price.id) {
              formData.append(`prices[${index}].id`, price.id);
            }
            formData.append(`prices[${index}].portugueseLabel`, price.portugueseLabel);
            formData.append(`prices[${index}].frenchLabel`, price.frenchLabel || '');
            formData.append(`prices[${index}].englishLabel`, price.englishLabel || '');
            formData.append(`prices[${index}].price`, price.price.toString().replace(',', '.'));
            formData.append(`prices[${index}].sortOrder`, index);
          });
        }

        // Handle image
        if (isEdit && typeof (data.photo) === 'string') {
          // Photo is still a URL string, don't send it
        } else if (data.photo && typeof (data.photo) === 'object') {
          formData.append('imageFile', data.photo);
        }

        let createdDish = null;
        if (!isEdit) {
          const res = await axiosInstance.post(API_ENDPOINTS.dish.post, formData, { headers: { "Content-Type": 'multipart/form-data' } });
          // API returns the new dish ID (Guid)
          createdDish = { id: res.data, ...data };
        } else {
          await axiosInstance.put(API_ENDPOINTS.dish.update, formData, { headers: { "Content-Type": 'multipart/form-data' } });
        }

        reset();
        onClose(createdDish);
        enqueueSnackbar(`${isEdit ? 'Atualização' : 'Adicionado'} com sucesso!`);
      } catch (error) {
        console.error(error);
        enqueueSnackbar('Erro ao guardar o prato', { variant: 'error' });
      }
    },
    [enqueueSnackbar, onClose, reset, isEdit]
  );

  const handleDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];

      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      if (file) {
        setValue('photo', newFile, { shouldValidate: true });
      }
    },
    [setValue]
  );

  const handleAddPrice = () => {
    appendPrice({
      portugueseLabel: '',
      frenchLabel: '',
      englishLabel: '',
      price: '',
      sortOrder: priceFields.length
    });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { maxWidth: 900 },
      }}
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>{isEdit ? 'Atualizar' : 'Adicionar'} prato</DialogTitle>

        <DialogContent>
          <Grid container spacing={3}>
            {/* Photo section */}
            <Grid xs={12} md={3}>
              <RHFUploadAvatar
                name="photo"
                maxSize={3145728}
                onDrop={handleDrop}
                helperText={
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 3,
                      mx: 'auto',
                      display: 'block',
                      textAlign: 'center',
                      color: 'text.disabled',
                    }}
                  >
                    Allowed *.jpeg, *.jpg, *.png, *.gif
                    <br /> max size of {fData(3145728)}
                  </Typography>
                }
              />
            </Grid>

            <Grid xs={12} md={9}>
              {/* Names section */}
              <Box
                rowGap={2}
                columnGap={2}
                display="grid"
                gridTemplateColumns={{
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(3, 1fr)',
                }}
                sx={{ p: 1 }}
              >
                <RHFTextField name="portugueseName" label="Nome (Português)" />
                <RHFTextField name="frenchName" label="Nome (Francês)" />
                <RHFTextField name="englishName" label="Nome (Inglês)" />
              </Box>

              {/* Descriptions section */}
              <Box sx={{ p: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Descrições</Typography>
                <Box
                  rowGap={2}
                  columnGap={2}
                  display="grid"
                  gridTemplateColumns={{
                    xs: 'repeat(1, 1fr)',
                    sm: 'repeat(3, 1fr)',
                  }}
                >
                  <RHFTextField name="portugueseDescription" label="Descrição (PT)" multiline rows={2} />
                  <RHFTextField name="frenchDescription" label="Descrição (FR)" multiline rows={2} />
                  <RHFTextField name="englishDescription" label="Descrição (EN)" multiline rows={2} />
                </Box>
              </Box>

              {/* Notes section */}
              <Box sx={{ p: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Notas</Typography>
                <Box
                  rowGap={2}
                  columnGap={2}
                  display="grid"
                  gridTemplateColumns={{
                    xs: 'repeat(1, 1fr)',
                    sm: 'repeat(3, 1fr)',
                  }}
                >
                  <RHFTextField name="portugueseNotes" label="Notas (PT)" multiline rows={2} />
                  <RHFTextField name="frenchNotes" label="Notas (FR)" multiline rows={2} />
                  <RHFTextField name="englishNotes" label="Notas (EN)" multiline rows={2} />
                </Box>
              </Box>

              {/* Category selection */}
              <Box sx={{ p: 1 }}>
                <RHFAutocomplete
                  name="category"
                  label="Categoria de pratos"
                  options={dishCategoriesData.map((option) => option)}
                  getOptionLabel={(option) => option?.portugueseName || ''}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option?.portugueseName}
                    </li>
                  )}
                  renderTags={(selected, getTagProps) =>
                    (selected.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option?.portugueseName}
                        size="small"
                        color="primary"
                        variant="soft"
                      />
                    )))
                  }
                />
              </Box>

              {/* Allergens selection */}
              <Box sx={{ p: 1 }}>
                <RHFAutocomplete
                  multiple
                  name="allergenIds"
                  label="Alergénios"
                  options={allergensData}
                  getOptionLabel={(option) => option?.portugueseName || ''}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option?.icon && <span style={{ marginRight: 8 }}>{option.icon}</span>}
                      {option?.portugueseName}
                    </li>
                  )}
                  renderTags={(selected, getTagProps) =>
                    selected.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={`${option?.icon || ''} ${option?.portugueseName}`}
                        size="small"
                        color="warning"
                        variant="soft"
                      />
                    ))
                  }
                />
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Pricing section */}
          <Box sx={{ p: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6">Preços</Typography>
              <RHFSwitch name="hasDetailedPricing" label="Modo detalhado (preços por quantidade)" />
            </Stack>

            {!hasDetailedPricing ? (
              <RHFTextField
                name="cost"
                label="Preço"
                placeholder="0.00"
                sx={{ maxWidth: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box sx={{ typography: 'subtitle2', color: 'text.disabled' }}>€</Box>
                    </InputAdornment>
                  ),
                  inputMode: 'decimal',
                }}
              />
            ) : (
              <Card variant="outlined" sx={{ p: 2 }}>
                <CardHeader
                  title="Preços por Quantidade"
                  subheader="Ex: Meia dose, Dose completa, Para 2 pessoas"
                  action={
                    <Button
                      size="small"
                      startIcon={<Iconify icon="mingcute:add-line" />}
                      onClick={handleAddPrice}
                    >
                      Adicionar Preço
                    </Button>
                  }
                  sx={{ p: 0, mb: 2 }}
                />
                <Stack spacing={2}>
                  {priceFields.map((field, index) => (
                    <Box
                      key={field.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr) auto' },
                        gap: 2,
                        alignItems: 'center',
                        p: 2,
                        bgcolor: 'background.neutral',
                        borderRadius: 1
                      }}
                    >
                      <RHFTextField
                        size="small"
                        name={`prices.${index}.portugueseLabel`}
                        label="Etiqueta (PT)"
                        placeholder="Ex: Meia Dose"
                      />
                      <RHFTextField
                        size="small"
                        name={`prices.${index}.frenchLabel`}
                        label="Etiqueta (FR)"
                        placeholder="Ex: Demi-portion"
                      />
                      <RHFTextField
                        size="small"
                        name={`prices.${index}.englishLabel`}
                        label="Etiqueta (EN)"
                        placeholder="Ex: Half portion"
                      />
                      <RHFTextField
                        size="small"
                        name={`prices.${index}.price`}
                        label="Preço"
                        placeholder="0.00"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">€</InputAdornment>
                          ),
                          inputMode: 'decimal',
                        }}
                      />
                      <IconButton color="error" onClick={() => removePrice(index)}>
                        <Iconify icon="solar:trash-bin-trash-bold" />
                      </IconButton>
                    </Box>
                  ))}
                  {priceFields.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      Clique em &quot;Adicionar Preço&quot; para adicionar opções de preço
                    </Typography>
                  )}
                </Stack>
              </Card>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            Cancelar
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            {isEdit ? 'Atualizar' : 'Adicionar'}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

DishQuickForm.propTypes = {
  current: PropTypes.object,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  isEdit: PropTypes.bool
};
